import { useState, useEffect } from "react";

import { DateRangePicker } from "rsuite";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import "rsuite/dist/rsuite.min.css";

import { EChart } from "../echarts/EChart";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import "./PowerChart.scss";

const { combine, allowedMaxDays, afterToday } = DateRangePicker;

// todo: add support to get average values on bigger intervals

const todayStart = new Date();
todayStart.setHours(0, 0, 0, 0);

const predefinedRanges = [
  {
    label: "Today",
    value: [todayStart, new Date()],
    placement: "left",
  },
  // {
  //   label: 'Yesterday',
  //   value: [addDays(new Date(), -1), addDays(new Date(), -1)],
  //   placement: 'left'
  // },
  {
    label: "This week",
    value: [startOfWeek(new Date()), endOfWeek(new Date())],
    placement: "left",
  },
  {
    label: "Last 7 days",
    value: [subDays(new Date(), 6), new Date()],
    placement: "left",
  },
  {
    label: "Last 30 days",
    value: [subDays(new Date(), 29), new Date()],
    placement: "left",
  },
  {
    label: "This month",
    value: [startOfMonth(new Date()), new Date()],
    placement: "left",
  },
  {
    label: "Last month",
    value: [
      startOfMonth(addMonths(new Date(), -1)),
      endOfMonth(addMonths(new Date(), -1)),
    ],
    placement: "left",
  },
  {
    label: "This year",
    value: [new Date(new Date().getFullYear(), 0, 1), new Date()],
    placement: "left",
  },
  {
    label: "Last year",
    value: [
      new Date(new Date().getFullYear() - 1, 0, 1),
      new Date(new Date().getFullYear(), 0, 0),
    ],
    placement: "left",
  },
  // {
  //   label: 'All time',
  //   value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date()],
  //   placement: 'left'
  // },
  {
    label: "Last week",
    closeOverlay: false,
    value: (value) => {
      const [start = new Date()] = value || [];
      return [
        addDays(startOfWeek(start, { weekStartsOn: 0 }), -7),
        addDays(endOfWeek(start, { weekStartsOn: 0 }), -7),
      ];
    },
    appearance: "default",
  },
  {
    label: "Next week",
    closeOverlay: false,
    value: (value) => {
      const [start = new Date()] = value || [];
      return [
        addDays(startOfWeek(start, { weekStartsOn: 0 }), 7),
        addDays(endOfWeek(start, { weekStartsOn: 0 }), 7),
      ];
    },
    appearance: "default",
  },
];

const now = new Date();
const defaultDateInterval = "day";
const defaultDateRange = [subDays(new Date(), 29), new Date()];
// new Date(now.setDate(now.getDate()-30)),
defaultDateRange[0].setHours(0, 0, 0);
defaultDateRange[1].setHours(0, 0, 0);

const defaultChartOptions = {
  // legend: {
  //   data: ["power"],
  //   // icon: "circle",
  //   // formatter: (name) => i18n.t(name),
  // },

  tooltip: {
    trigger: "axis",
    axisPointer: {
      type: "line",
      snap: true,
    },
    confine: true,
    valueFormatter: function (params) {
      return `${params} kW`;
    },
  },

  xAxis: [
    {
      id: "xAxis1",
      type: "time",
      boundaryGap: false,
      // splitLine: {
      //   show: true,
      // },
      // axisLabel: {
      //   hideOverlap: true,
      // },
    },
  ],
  yAxis: [
    {
      type: "value",
      name: "kW",
      // position: "left",
      nameLocation: "middle",
      nameGap: 50,
      // axisLabel: {
      //   formatter: (value) => {
      //     return value;
      //   },
      // },
    },
  ],
  series: [
    {
      type: "line",
      showSymbol: false,
      // smooth: true,
      itemStyle: {
        color: "orange",
      },
    },
  ],
};

const PowerChart = ({ meter, aspect, title }) => {
  const axiosPrivate = useAxiosPrivate();

  const [interval, setInterval] = useState("month");
  const [customDateSet, setCustomDateSet] = useState(false);

  const [dateRange, setDateRange] = useState(defaultDateRange);

  const [multiplePhases, setMultiplePhases] = useState(false);
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    setChartOptions(defaultChartOptions);
  }, []);

  useEffect(() => {
    if (dateRange) {
      fetchChartData(meter.id, dateRange).then((res) => {
        const data = res.map((d) => [d.t, (d.power / 1000.0).toFixed(3)]);
        setChartOptions({
          series: [
            {
              data: data,
            },
          ],
        });
      });
    }

    // if (data.find(m => m.power_l1 || m.power_l1 || m.power_l1)) {
    //   setMultiplePhases(true)
    // }
  }, [dateRange, interval]);

  const fetchChartData = async (meterId, date) => {
    try {
      const from = date[0];
      const to = date[1];
      to.setDate(to.getDate() + 1);
      const url = `/meters/${
        meter.id
      }/metrics/power?interval=${interval}&from=${from.toISOString()}&to=${to.toISOString()}`
      const res = await axiosPrivate.get(url, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return res.data;
    } catch (err) {
      console.log(err);
      if (err.response.status === 401 || err.response.status === 403) {
        // redirect to login if unauthorized
        // navigate('/login', { state: { from: location, unauthorized: true }, replace: true });
      } else {
        // setError(err)
      }
    }
    // setLoading(false)
  };

  return (
    <div className="chart">
      <div className="top">
        <div className="title">{title}</div>
        <div className="inputs">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            // character=" - "
            placement="bottomEnd"
            showOneCalendar
            showWeekNumbers
            ranges={predefinedRanges}
            // appearance="default"
            // placeholder="Default"
            style={{ width: 230 }}
            format="yyyy-MM-dd"
            cleanable={false}
            // shouldDisableDate={afterToday()}
          />
        </div>
      </div>

      <EChart option={chartOptions} />
    </div>
  );
};

export default PowerChart;
